import dayjs from 'dayjs'
import { useForm } from 'vee-validate'
import * as yup from 'yup'
import { EndType, IntervalType, ScheduleRRuleOptions, ShortMonths, ShortWeekDays } from '../../const'
import i18n from '../../locales'
import { getCurrentDate, getFormatDate } from '../../utils'

const ALLOWED_DAYS = [...Array.from(Array(31), (_, i) => i + 1), -1]

export const RRuleSchema = yup.object({
  intervalType: yup
    .mixed()
    .oneOf(Object.values(IntervalType), i18n.global.t('validations.enum.intervalType'))
    .required(i18n.global.t('validations.invalid.intervalType')),
  interval: yup
    .number()
    .typeError(i18n.global.t('validations.invalid.numberInterval'))
    .max(99)
    .positive(i18n.global.t('validations.invalid.positiveInterval'))
    .integer(i18n.global.t('validations.invalid.integerInterval'))
    .required(i18n.global.t('validations.invalid.interval')),

  dayOfMonth: yup.mixed().when(['intervalType', 'numOfWeek', 'monthOfYear'], (intervalType, numOfWeek, monthOfYear) => {
    if ((intervalType === IntervalType.MONTHLY && numOfWeek === 0) || intervalType === IntervalType.YEARLY) {
      return yup
        .mixed()
        .oneOf(ALLOWED_DAYS, i18n.global.t('validations.enum.dayOfMonth'))
        .required(i18n.global.t('validations.invalid.dayOfMonth'))
        .test('yearly-day-in-month', i18n.global.t('validations.invalid.dayOfMonth'), function (value) {
          if (intervalType !== IntervalType.YEARLY) return true
          const monthKey = monthOfYear?.toLowerCase()
          const day = Number(value)
          if (!monthKey) return true
          // Feb: 30 and 31 invalid
          if (monthKey === 'feb' && (day === 30 || day === 31)) return false
          // Apr, Jun, Sep, Nov: 31 invalid
          if (['apr', 'jun', 'sep', 'nov'].includes(monthKey) && day === 31) return false
          return true
        })
    }
  }),
  dayOfWeek: yup.mixed().when(['intervalType', 'numOfWeek'], (intervalType, numOfWeek) => {
    if (intervalType === IntervalType.WEEKLY || (intervalType === IntervalType.MONTHLY && numOfWeek !== 0)) {
      return yup
        .mixed()
        .oneOf(Object.values(ShortWeekDays), i18n.global.t('validations.enum.dayOfWeek'))
        .required(i18n.global.t('validations.invalid.dayOfWeek'))
    }
  }),
  numOfWeek: yup.mixed().when('intervalType', intervalType => {
    if (intervalType === IntervalType.MONTHLY)
      return yup
        .mixed()
        .oneOf([-1, 0, 1, 2, 3, 4], i18n.global.t('validations.enum.numOfWeek'))
        .required(i18n.global.t('validations.invalid.numOfWeek'))
  }),
  monthOfYear: yup.mixed().when('intervalType', intervalType => {
    if (intervalType === IntervalType.YEARLY)
      return yup
        .mixed()
        .oneOf(Object.values(ShortMonths), i18n.global.t('validations.enum.monthOfYear'))
        .required(i18n.global.t('validations.invalid.monthOfYear'))
  }),
  startDate: yup.mixed().when('useStartAsPrimaryUserSigned', useStartAsPrimaryUserSigned => {
    if (!useStartAsPrimaryUserSigned)
      return yup
        .date()
        .typeError(i18n.global.t('validations.invalid.dateStartDate'))
        .min(getCurrentDate(), `${i18n.global.t('validations.min.startDate')} ${getCurrentDate()}`)
        .required(i18n.global.t('validations.invalid.startDate'))
  }),
  startTime: yup.mixed().when('intervalType', intervalType => {
    if ([IntervalType.MINUTELY, IntervalType.HOURLY].includes(intervalType))
      return yup.string().typeError(i18n.global.t('validations.invalid.stringStartTime'))
  }),
  endType: yup
    .mixed()
    .oneOf(Object.values(EndType), i18n.global.t('validations.enum.endType'))
    .required(i18n.global.t('validations.invalid.endType')),
  endDate: yup.mixed().when(['endType', 'startDate'], (endType, startDate) => {
    if (endType === EndType.BY) {
      return yup
        .date()
        .typeError(i18n.global.t('validations.invalid.dateEndDate'))
        .test('is-greater-than', i18n.global.t('validations.min.endDate'), function (val) {
          const { path, createError } = this
          const currentDate = getCurrentDate()
          const start = startDate && startDate !== 'Invalid Date' ? getFormatDate(startDate) : null

          const isPastCurrantDate = dayjs(val).isBefore(currentDate, 'day')
          const isPastStartDate = start ? dayjs(val).isBefore(start, 'day') : false

          if (isPastCurrantDate) {
            return createError({
              path,
              message: `${i18n.global.t('validations.min.endDate')} ${getCurrentDate()}`,
            })
          }

          if (isPastStartDate) {
            return createError({
              path,
              message: `${i18n.global.t('validations.min.endDate')} ${getFormatDate(startDate)}`,
            })
          }

          return true
        })
        .required(i18n.global.t('validations.invalid.endDate'))
    }
  }),
  endTime: yup.mixed().when(['endDate', 'intervalType'], (endDate, intervalType) => {
    if ([IntervalType.MINUTELY, IntervalType.HOURLY].includes(intervalType) && endDate)
      return yup.string().required(i18n.global.t('validations.invalid.endTime'))
  }),
  count: yup.mixed().when('endType', endType => {
    if (endType === EndType.AFTER)
      return yup
        .number()
        .typeError(i18n.global.t('validations.invalid.numberCount'))
        .max(99)
        .nullable()
        .positive(i18n.global.t('validations.invalid.positiveCount'))
        .integer()
        .required(i18n.global.t('validations.invalid.count'))
  }),
  daysBefore: yup.mixed().when(['intervalType', 'useStartAsPrimaryUserSigned'], (intervalType, useStartAsPrimaryUserSigned) => {
    if (!useStartAsPrimaryUserSigned && [IntervalType.WEEKLY, IntervalType.MONTHLY, IntervalType.YEARLY].includes(intervalType)) {
      const max = intervalType === IntervalType.WEEKLY ? 7 : 28
      return yup
        .number()
        .typeError(i18n.global.t('validations.invalid.numberDaysBefore'))
        .min(0)
        .max(max, `${i18n.global.t('validations.max.daysBefore')} ${max}`)
        .nullable()
        .integer()
        .required(i18n.global.t('validations.invalid.daysBefore'))
    }
  }),
  useStartAsPrimaryUserSigned: yup
    .boolean()
    .typeError(i18n.global.t('validations.invalid.booleanUseStartAsPrimaryUserSigned'))
    .required(i18n.global.t('validations.required.useStartAsPrimaryUserSigned')),
})

export const RRuleSchemaV2 = yup.object({
  intervalType: yup
    .mixed()
    .oneOf(Object.values(IntervalType), i18n.global.t('validations.enum.intervalType'))
    .required(i18n.global.t('validations.invalid.intervalType')),

  endType: yup
    .mixed()
    .oneOf(Object.values(EndType), i18n.global.t('validations.enum.endType'))
    .required(i18n.global.t('validations.invalid.endType')),
  endDate: yup.mixed().when(['endType', 'startDate'], (endType, startDate) => {
    if (endType === EndType.BY) {
      return yup
        .date()
        .typeError(i18n.global.t('validations.invalid.dateEndDate'))
        .test('is-greater-than', i18n.global.t('validations.min.endDate'), function (val) {
          const { path, createError } = this
          const currentDate = getCurrentDate()
          const start = startDate && startDate !== 'Invalid Date' ? getFormatDate(startDate) : null

          const isPastCurrantDate = dayjs(val).isBefore(currentDate, 'day')
          const isPastStartDate = start ? dayjs(val).isBefore(start, 'day') : false

          if (isPastCurrantDate) {
            return createError({
              path,
              message: `${i18n.global.t('validations.min.endDate')} ${getCurrentDate()}`,
            })
          }

          if (isPastStartDate) {
            return createError({
              path,
              message: `${i18n.global.t('validations.min.endDate')} ${getFormatDate(startDate)}`,
            })
          }

          return true
        })
        .required(i18n.global.t('validations.invalid.endDate'))
    }
  }),
  endTime: yup.mixed().when(['endDate', 'intervalType'], (endDate, intervalType) => {
    if ([IntervalType.MINUTELY, IntervalType.HOURLY].includes(intervalType) && endDate)
      return yup.string().required(i18n.global.t('validations.invalid.endTime'))
  }),
  count: yup.mixed().when('endType', endType => {
    if (endType === EndType.AFTER)
      return yup
        .number()
        .typeError(i18n.global.t('validations.invalid.numberCount'))
        .max(99)
        .nullable()
        .positive(i18n.global.t('validations.invalid.positiveCount'))
        .integer()
        .required(i18n.global.t('validations.invalid.count'))
  }),
  daysBefore: yup.mixed().when(['intervalType', 'useStartAsPrimaryUserSigned'], (intervalType, useStartAsPrimaryUserSigned) => {
    if (!useStartAsPrimaryUserSigned && [IntervalType.WEEKLY, IntervalType.MONTHLY, IntervalType.YEARLY].includes(intervalType)) {
      const max = intervalType === IntervalType.WEEKLY ? 7 : 28
      return yup
        .number()
        .typeError(i18n.global.t('validations.invalid.numberDaysBefore'))
        .min(0)
        .max(max, `${i18n.global.t('validations.max.daysBefore')} ${max}`)
        .nullable()
        .integer()
        .required(i18n.global.t('validations.invalid.daysBefore'))
    }
  }),
})

export const getSchedulingSettingsForm = (rrule: ScheduleRRuleOptions) => {
  return useForm({
    validationSchema: RRuleSchema,
    initialValues: {
      intervalType: rrule?.intervalType || '',
      interval: rrule?.interval || 0,
      dayOfMonth: rrule?.dayOfMonth || '',
      dayOfWeek: rrule?.dayOfWeek || '',
      numOfWeek: rrule?.numOfWeek || 0,
      monthOfYear: rrule?.monthOfYear,
      startTime: rrule?.startTime || '',
      endType: rrule?.endType || '',
      endTime: rrule?.endTime || '',
      count: rrule?.count || '',
      daysBefore: rrule?.daysBefore || 0,
      startDate: rrule?.startDate || '',
      endDate: rrule?.endDate || '',
      useStartAsPrimaryUserSigned: rrule?.useStartAsPrimaryUserSigned ?? true,
    },
  })
}

export const getSchedulingSettingsFormV2 = (rrule: ScheduleRRuleOptions) => {
  return useForm({
    validationSchema: RRuleSchemaV2,
    initialValues: {
      intervalType: (rrule?.intervalType || IntervalType.WEEKLY) as IntervalType,
      interval: rrule?.interval || 0,
      dayOfMonth: rrule?.dayOfMonth || '',
      dayOfWeek: rrule?.dayOfWeek || '',
      numOfWeek: rrule?.numOfWeek || 0,
      monthOfYear: rrule?.monthOfYear,
      startTime: rrule?.startTime || '',
      endType: rrule?.endType || EndType.NEVER,
      endTime: rrule?.endTime || '',
      count: rrule?.count || '',
      daysBefore: rrule?.daysBefore || 0,
      startDate: rrule?.startDate || '',
      endDate: rrule?.endDate || '',
      useStartAsPrimaryUserSigned: rrule?.useStartAsPrimaryUserSigned ?? true,
    },
  })
}

export const initialiseScheduleErrorsIfNotExists = (schedule: ScheduleRRuleOptions, generateInvoiceOnSigning: boolean) => {
  if (generateInvoiceOnSigning) {
    const { validate } = getSchedulingSettingsFormV2(schedule)
    return validate()
  } else {
    const { validate } = getSchedulingSettingsForm(schedule)
    return validate()
  }
}
